import { Table } from '@tanstack/react-table';

export interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  currentPageIndex: number;
  setCurrentPageIndex: (index: number) => void;
  rowCount: number;
  resourceName?: string;
  isFetchingNextPage?: boolean;
}

interface UseDataTablePaginationReturn {
  pageIndex: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  pageCount: number;
  handlePreviousPage: () => void;
  handleNextPage: () => void;
}

export const useDataTablePagination = <TData,>({
  table,
  fetchNextPage,
  hasNextPage,
  currentPageIndex = 0,
  setCurrentPageIndex,
  rowCount,
  isFetchingNextPage,
}: DataTablePaginationProps<TData>): UseDataTablePaginationReturn => {
  // If custom pagination is provided, use it. Otherwise, fall back to table's internal pagination
  const pageIndex = setCurrentPageIndex ? currentPageIndex : table.getState().pagination.pageIndex;

  const pageSize = table.getState().pagination.pageSize;
  const pageCount = Math.ceil(rowCount / pageSize) || 0;

  const canPreviousPage = setCurrentPageIndex ? pageIndex > 0 : table.getCanPreviousPage();

  const canNextPage = setCurrentPageIndex
    ? hasNextPage && !isFetchingNextPage
    : (table.getCanNextPage() || hasNextPage) && !isFetchingNextPage;

  const handlePreviousPage = (): void => {
    if (pageIndex > 0) {
      if (setCurrentPageIndex) {
        setCurrentPageIndex(pageIndex - 1);
      } else {
        table.previousPage();
      }
    }
  };

  const handleNextPage = (): void => {
    if (setCurrentPageIndex) {
      // Always try to fetch next page if available, since we're using infinite scroll pagination
      if (hasNextPage && fetchNextPage) {
        fetchNextPage();
      }
    } else {
      // Use the default behavior if we're not using custom pagination
      if (fetchNextPage && hasNextPage) {
        fetchNextPage();
      } else {
        table.nextPage();
      }
    }
  };

  return { pageIndex, pageCount, canPreviousPage, canNextPage, handlePreviousPage, handleNextPage };
};
